import { AlertTriangle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { QuotationStatus } from "@shared/schema";

interface QuotationStatusBannerProps {
  status?: QuotationStatus | null;
  className?: string;
  titleLevel?: "h2" | "h3" | "h4" | "h5";
}

function formatAge(seconds: number | null): string {
  if (seconds == null) return "";
  if (seconds < 60) return `${seconds} secondi`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minuti`;
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  if (remMin === 0) return `${hours} ${hours === 1 ? "ora" : "ore"}`;
  return `${hours} ${hours === 1 ? "ora" : "ore"} e ${remMin} min`;
}

/**
 * Customer-facing notice that appears on public pages displaying metal
 * quotations whenever the upstream BullionVault feed is degraded:
 *  - `stale`    → cached prices older than 30 minutes
 *  - `fallback` → hardcoded fallback prices (no cache available)
 *
 * Renders nothing when the data is fresh (`live`/`cache`).
 */
export function QuotationStatusBanner({
  status,
  className,
  titleLevel = "h5",
}: QuotationStatusBannerProps) {
  if (!status) return null;
  if (!status.isStale && !status.isFallback) return null;

  const isFallback = status.isFallback;
  const ageLabel = formatAge(status.ageSeconds);

  const title = isFallback
    ? "Prezzi indicativi — feed di mercato temporaneamente non disponibile"
    : "Prezzi indicativi — feed di mercato temporaneamente non aggiornato";

  const description = isFallback
    ? "Le quotazioni mostrate sono indicative perché il feed di mercato live è momentaneamente irraggiungibile. Per il prezzo definitivo contatta il nostro ufficio."
    : `Le quotazioni mostrate risalgono a ${ageLabel} fa: il feed di mercato non si aggiorna in tempo reale al momento. Sono prezzi indicativi — per il prezzo definitivo contatta il nostro ufficio.`;
  const StatusTitle = titleLevel;

  return (
    <Alert
      variant="destructive"
      className={className}
      data-testid={`alert-quotations-${isFallback ? "fallback" : "stale"}`}
    >
      <AlertTriangle className="h-4 w-4" />
       <StatusTitle className="mb-1 font-medium leading-none tracking-tight" data-testid="text-quotations-status-title">{title}</StatusTitle>
      <AlertDescription data-testid="text-quotations-status-description">
        {description}
      </AlertDescription>
    </Alert>
  );
}

interface QuotationStatusIndicatorProps {
  status?: QuotationStatus | null;
  className?: string;
  /** Optional testid suffix so multiple indicators on a page stay unique. */
  testIdSuffix?: string;
}

/**
 * Compact inline stale/fallback indicator for space-constrained price surfaces
 * (header ticker, footer price strip). Renders a small AlertTriangle with an
 * accessible label/title; nothing when the feed is fresh.
 */
export function QuotationStatusIndicator({
  status,
  className,
  testIdSuffix,
}: QuotationStatusIndicatorProps) {
  if (!status) return null;
  if (!status.isStale && !status.isFallback) return null;

  const isFallback = status.isFallback;
  const label = isFallback
    ? "Prezzi indicativi — feed di mercato non disponibile"
    : "Prezzi indicativi — feed di mercato non aggiornato";

  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={`inline-flex items-center ${className ?? ""}`}
      data-testid={`indicator-quotations-stale${testIdSuffix ? `-${testIdSuffix}` : ""}`}
    >
      <AlertTriangle className="h-3.5 w-3.5 text-amber-500" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </span>
  );
}
